import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/stores/authStore';

interface EffectivePermissionsDto {
  permissions: string[];
}

/**
 * Story 45.6 — per-principal effective permissions hook.
 *
 * Fetches the calling user's effective permission set from
 * GET /api/v1/permissions/me/effective once on mount (or on auth change)
 * and exposes a `can(key)` helper so components can conditionally
 * render UI elements without repeating HTTP calls.
 *
 * PLATFORM_ADMIN users always get `can()` returning true for every key
 * because the backend already bypasses all checks for them; the
 * `permissions` set returned will contain 'platform.admin'.
 */
export function usePermissions() {
  const tokens = useAuthStore((s) => s.tokens);
  const role = useAuthStore((s) => s.user?.role);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tokens) {
      setPermissions(new Set());
      return;
    }

    setLoading(true);
    apiClient
      .get<EffectivePermissionsDto>('/permissions/me/effective')
      .then(({ data }) => setPermissions(new Set(data.permissions)))
      .catch(() => setPermissions(new Set()))
      .finally(() => setLoading(false));
  }, [tokens]);

  const can = useCallback(
    (key: string): boolean => {
      // PLATFORM_ADMIN bypasses all checks (mirrors backend behaviour).
      if (role === 'PLATFORM_ADMIN') return true;
      return permissions.has(key);
    },
    [permissions, role],
  );

  return { can, permissions, loading };
}
